import {
  SelectionSet,
  SelectionSetInput,
  SelectionSetLabel,
  SelectionSetTileImageContainer,
  FluidImage,
  SelectionSetTileContents,
  SelectionSetTileName,
  SelectionSetTilePrice,
  Button,
  Box,
  FlexBox,
  StatusMessage,
} from '@vp/swan';
import React, { useContext, useEffect, useState } from 'react';

import './ProductList.styles.scss';
import { IOrderProductData, IProductSelectData } from '../../../interfaces/order-tracking-flow-interfaces';
import { ChatbotContext } from '../../../contexts/ChatbotContext';

interface IProductCardProps {
  sender: 'chatbot' | 'user';
  orderId: string;
  items: IOrderProductData[];
  isCancellationFlow?: boolean;
  onClick?: () => void;
}

const ProductList: React.FC<IProductCardProps> = ({ items, orderId, sender, isCancellationFlow }) => {
  const {
    state: { sendChatbotMessage },
  } = useContext(ChatbotContext);
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: boolean }>({});
  const [showConfirmButton, setShowConfirmButton] = useState(false);

  const [showWarningMessage, setShowWarningMessage] = useState(false);

  const handleProductSelect = () => {
    if (sender === 'user') return; // Prevent selection if sender is user
    const selectedItems = items.filter((item) => selectedValues[item.itemId]);
    console.log('Selected items:', selectedItems);
    setShowConfirmButton(false);
    if (selectedItems && sendChatbotMessage) {
      sendChatbotMessage({
        type: 'product_select',
        messageData: {
          items: selectedItems,
          orderId,
        } as IProductSelectData,
      });
    }
    // const acc:{ [key:string]: boolean } = {};
    // items.forEach((item) => {
    //   acc[item.itemId] = false;
    // });
    // setSelectedValues(acc);
  };

  const handleSelectedValuesChange = (e: { [key: string]: boolean }) => {
    if (sender === 'user') return; // Prevent selection changes if sender is user
    setSelectedValues(e);
    let isSelected = false;
    Object.values(e).forEach((value) => {
      if (value) {
        isSelected = true;
      }
    });
    setShowConfirmButton(isSelected);
  };

  /** Initial render, all elements to be de-selected */
  useEffect(() => {
    const acc: { [key: string]: boolean } = {};
    items.forEach((item) => {
      acc[item.itemId] = false;
    });
    setSelectedValues(acc);
  }, [items]);

  useEffect(() => {
    if (isCancellationFlow) {
      const hasCancellableItem = items.some((product) => product.is_cancellable === false);
      if (hasCancellableItem) {
        setShowWarningMessage(true);
      }
    }
  }, [items]);

  return (
    <Box>
      {isCancellationFlow && showWarningMessage && (
        <StatusMessage skin="warning" marginY="between-actions" style={{ paddingTop: '6px' }}>
          Cancellation isn’t possible once we start printing your products.
        </StatusMessage>
      )}
      <SelectionSet
        skin="tiles-mini"
        variant="multi-select"
        compactMode
        selectedValues={selectedValues}
        onSelectedValuesChange={handleSelectedValuesChange}
        imageWidth="fixed"
        className={`product-list ${sender === 'user' ? 'ca-product-list-user ' : ''}`}
        marginY="between-actions"
        style={sender === 'user' ? { cursor: 'default' } : {}}
        marginTop={'between-actions'}
      >
        {items.map((item) => (
          <SelectionSetInput value={item.itemId} key={item.itemId} disabled={isCancellationFlow && !item.is_cancellable}>
            <SelectionSetLabel className="product-container" style={{ marginBottom: '8px' }}>
              <SelectionSetTileImageContainer className="product-image-container">
                <FluidImage src={item.imagURL} alt="placeholder" style={{ maxHeight: '60px', maxWidth: '60px' }} />
              </SelectionSetTileImageContainer>
              <SelectionSetTileContents className="product-description">
                <SelectionSetTileName>{item.name}</SelectionSetTileName>
                <SelectionSetTilePrice>
                  {isCancellationFlow ? (!item.is_cancellable ? item.status : item.displayPrice) : item.displayPrice}
                </SelectionSetTilePrice>
              </SelectionSetTileContents>
            </SelectionSetLabel>
          </SelectionSetInput>
        ))}
      </SelectionSet>
      {showConfirmButton && (
        <FlexBox justifyContent="space-around" className="product-list-confirm-button">
          <Button skin="primary" onClick={handleProductSelect}>
            Confirm selection
          </Button>
        </FlexBox>
      )}
    </Box>
  );
};

export default ProductList;
